import {createContext, use, useMemo, useState} from 'react'
import type {Dispatch, ReactNode, SetStateAction} from 'react'
import type {SidebarLink} from '@github-ui/saved-views'
import {useLocation} from '@github-ui/react-router'
import {deriveSidebarLink} from '@github-ui/shared-dashboard-surface/helpers/layout-helpers'

import type {IssuesDashboardLayoutPayload} from '../types/routes-types'
import {useIssuesListResults} from '../hooks/use-issues-list-results'
import {ISSUES_DASHBOARD_PATH} from '../constants/routes'

interface IssuesDashboardSurfaceStateContextValue {
  payload: IssuesDashboardLayoutPayload
  fetchedQuery: string
  currentPage: number
  locationSidebarLinkId?: string
  isLoading: boolean
  fetchResults: (args: {q: string; page: number; sidebarLinkId?: string}) => void
}

const IssuesDashboardSurfaceStateContext = createContext<IssuesDashboardSurfaceStateContextValue | null>(null)
const IssuesDashboardSavedViewsContext = createContext<SidebarLink[] | null>(null)
const IssuesDashboardSelectedSidebarLinkContext = createContext<SidebarLink | undefined | null>(null)
const SetIssuesDashboardSavedViewsContext = createContext<Dispatch<SetStateAction<SidebarLink[]>> | null>(null)
const SetIssuesDashboardSelectedSidebarLinkContext = createContext<Dispatch<
  SetStateAction<SidebarLink | undefined>
> | null>(null)

export function IssuesDashboardSurfaceStateProvider({
  children,
  payload,
  isLoading,
}: {
  children: ReactNode
  payload: IssuesDashboardLayoutPayload
  isLoading: boolean
}) {
  const location = useLocation()
  const {fetchedQuery, currentPage, fetchResults, locationSidebarLinkId} = useIssuesListResults({
    defaultFilterValue: '',
  })
  const [savedViews, setSavedViews] = useState<SidebarLink[]>(() =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- assertion required for type narrowing; removing it breaks tsc
    payload.savedViews.map(savedView => ({...savedView, searchType: 'issues'}) as SidebarLink),
  )
  const [selectedSidebarLink, setSelectedSidebarLink] = useState<SidebarLink | undefined>(() =>
    deriveSidebarLink({
      sidebarLinkId: locationSidebarLinkId,
      basePath: ISSUES_DASHBOARD_PATH,
      savedViews,
      sidebarLinks: payload.sidebarLinks ?? [],
      location,
    }),
  )

  const value: IssuesDashboardSurfaceStateContextValue = useMemo(
    () => ({
      payload,
      fetchedQuery,
      currentPage,
      fetchResults,
      locationSidebarLinkId,
      isLoading,
    }),
    [payload, fetchedQuery, currentPage, fetchResults, locationSidebarLinkId, isLoading],
  )

  return (
    <IssuesDashboardSurfaceStateContext value={value}>
      <SetIssuesDashboardSavedViewsContext value={setSavedViews}>
        <SetIssuesDashboardSelectedSidebarLinkContext value={setSelectedSidebarLink}>
          <IssuesDashboardSavedViewsContext value={savedViews}>
            <IssuesDashboardSelectedSidebarLinkContext value={selectedSidebarLink}>
              {children}
            </IssuesDashboardSelectedSidebarLinkContext>
          </IssuesDashboardSavedViewsContext>
        </SetIssuesDashboardSelectedSidebarLinkContext>
      </SetIssuesDashboardSavedViewsContext>
    </IssuesDashboardSurfaceStateContext>
  )
}

export function useIssuesDashboardSurfaceStateContext() {
  const context = use(IssuesDashboardSurfaceStateContext)
  if (!context) {
    throw new Error('useIssuesDashboardSurfaceStateContext must be used within an IssuesDashboardSurfaceStateProvider')
  }
  return context
}

export function useIssuesDashboardSavedViews() {
  const context = use(IssuesDashboardSavedViewsContext)
  if (!context) {
    throw new Error('useIssuesDashboardSavedViews must be used within an IssuesDashboardSurfaceStateProvider')
  }
  return context
}

export function useIssuesDashboardSelectedSidebarLink() {
  const context = use(IssuesDashboardSelectedSidebarLinkContext)
  if (context === null) {
    throw new Error('useIssuesDashboardSelectedSidebarLink must be used within an IssuesDashboardSurfaceStateProvider')
  }
  return context
}

export function useSetIssuesDashboardSavedViews() {
  const context = use(SetIssuesDashboardSavedViewsContext)
  if (!context) {
    throw new Error('useSetIssuesDashboardSavedViews must be used within an IssuesDashboardSurfaceStateProvider')
  }
  return context
}

export function useSetIssuesDashboardSelectedSidebarLink() {
  const context = use(SetIssuesDashboardSelectedSidebarLinkContext)
  if (!context) {
    throw new Error(
      'useSetIssuesDashboardSelectedSidebarLink must be used within an IssuesDashboardSurfaceStateProvider',
    )
  }
  return context
}
